<script>
import pluralize from 'pluralize'
import { inject } from 'vue'

export default {
  name: 'DataTableBatchActions',
  emits: ['updateCheckedItemIds'],
  props: {
    actions: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    itemIdsOnPage: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    inProgress: { type: Boolean, default: false },
    allRecordsTextAddon: { type: String, default: 'records' },
    isNarrow: { type: Boolean, default: false },
    dataModelType: { type: String, default: null },
    allowDelete: { type: Boolean, default: false },
    humanizedDataModelName: { type: String, default: null },
    dataModelService: { type: Object, default: null },
  },
  data() {
    return {
      allChecked: false,
      allOnPageChecked: false,
      checkedItems: [],
    }
  },
  setup() {
    const can = inject('can', () => false)

    return {
      can,
    }
  },
  computed: {
    currentNumberOfCheckedItems() {
      if (this.allChecked) {
        return this.total
      } else {
        return this.checkedItems.length
      }
    },
    actionsWithLocalItems() {
      const result = [...this.actions]
      result.unshift({ type: 'divider' })
      if (!this.allChecked)
        result.unshift({
          title: `Check all ${this.total} ${this.allRecordsTextAddon}`,
          clickFn: () => {
            this.toggleAll(true)
          },
        })
      if (this.allChecked || this.checkedItems.length > 0)
        result.unshift({
          title: 'Uncheck',
          clickFn: () => {
            this.toggleAll(false)
          },
        })

      if (this.allowDelete) {
        result.push({
          title: 'Remove',
          iconClasses: 'mi-outlined mi-delete',
          classes: 'color-error',
          clickFn: async () => {
            await this.$dialogs.confirm(
              `Are you sure that you want to remove ${
                this.checkedItems.length
              } selected ${pluralize(this.humanizedDataModelName, this.checkedItems.length)}?`,
            )
            try {
              await this.dataModelService.dispatchAction('batch_destroy', null, {
                data: {
                  ids: this.checkedItems,
                },
              })
              this.$success({
                message: `Successfully removed ${this.checkedItems.length} ${pluralize(
                  this.humanizedDataModelName,
                  this.checkedItems.length,
                )}`,
              })
            } catch (e) {
              this.$error({
                message: `Error while removing ${pluralize(
                  this.humanizedDataModelName,
                  this.checkedItems.length,
                )}`,
              })
            }
          },
          disabled: !this.checkedItems.length,
        })
      }
      return result
    },
  },
  watch: {
    itemIdsOnPage() {
      this.recomputeAllOnPageChecked()
    },
  },
  methods: {
    cleanAll() {
      this.allChecked = false
      this.checkedItems.length = 0
      this.allOnPageChecked = false
    },
    recomputeAllOnPageChecked() {
      let result
      if (this.allChecked) {
        this.checkedItems = [...this.itemIdsOnPage]
        this.$emit('updateCheckedItemIds', this.checkedItems)
        result = true
      } else if (
        this.itemIdsOnPage.length > 0 &&
        Array.from(new Set([this.checkedItems, this.itemIdsOnPage].flat())).length ==
          this.checkedItems.length
      ) {
        result = true
      } else {
        result = false
      }
      this.allOnPageChecked = result
      return result
    },
    toggleAll(newValue) {
      this.allChecked = newValue
      if (!newValue) {
        this.checkedItems = []
        this.$emit('updateCheckedItemIds', this.checkedItems)
      }
      this.recomputeAllOnPageChecked()
    },
    toggleAllOnPage(newValue) {
      if (newValue) {
        if (!this.allChecked) {
          this.checkedItems = Array.from(new Set([this.checkedItems, this.itemIdsOnPage].flat()))
          this.$emit('updateCheckedItemIds', this.checkedItems)
        }
      } else {
        this.checkedItems = this.checkedItems.filter(el => !this.itemIdsOnPage.includes(el))
        this.$emit('updateCheckedItemIds', this.checkedItems)
      }
      return this.recomputeAllOnPageChecked()
    },
    toggledRow(itemId) {
      if (this.allChecked) {
        return
        // this.checkedItems = [...this.itemIdsOnPage]
      }
      if (this.checkedItems.includes(itemId)) {
        this.checkedItems.splice(this.checkedItems.indexOf(itemId), 1)
        this.$emit('updateCheckedItemIds', this.checkedItems)
      } else {
        this.checkedItems.push(itemId)
        this.$emit('updateCheckedItemIds', this.checkedItems)
      }
      this.recomputeAllOnPageChecked()
    },
  },
}
</script>

<template lang="pug">
dropdown.ms-2(:actions="actionsWithLocalItems" alignRight)
  .btn.btn-md.btn-outline-primary.dropdown-toggle.no-right-radius(
    :class="{ disabled: disabled }"
    name="BatchActions"
  )
    .d-inline-flex.align-items-center
      .me-2
        .spinner-border.text-info.me-2(v-if="inProgress")
        span.data-table-batch-actions-label Batch{{ isNarrow ? '' : ' actions' }}
        span.data-table-batch-actions-label-small Batch
        slot
          span.bold.me-1(v-if="currentNumberOfCheckedItems > 0") |{{ currentNumberOfCheckedItems }}
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;
@import 'frontend_stylesheets/_variables.scss';
.spinner-border {
  height: 1em;
  width: 1em;
  margin-top: 2px;
  display: inline-block;
}

.data-table-batch-actions-label {
  @include for-size(small-and-medium-devices) {
    display: none;
  }
}

.data-table-batch-actions-label-small {
  display: none;
  @include for-size(small-and-medium-devices) {
    display: inline;
  }
}

.separator {
  height: 28px;
  margin-top: 6px;
  width: 1px;
  background: map.get(themeVariables.$colors, outlineVariant);
  //margin: 0 4px;
}
</style>
